/**
 * État des sauvegardes (voyant de l'en-tête, super admin seulement).
 * Appelle GET /api/ops/status : le backend relit le relevé poussé par le PC et renvoie une réponse en liste blanche
 * (aucune ligne de log, aucun chemin, aucun hôte). Le contrôle du super admin est fait par le serveur, pas ici.
 */
import { authenticatedFetch } from '@/utils/authenticatedFetch';

export type BackupLevel = 'green' | 'orange' | 'red' | 'gray';

export interface BackupStatus {
  schema: number;
  generatedAt: string;
  level: BackupLevel;
  /** gray seulement : no_releve (aucun relevé lisible) ou off_hours (hors service prévu) */
  reason: 'no_releve' | 'off_hours' | null;
  summary: string;
  openDay: boolean;
  inService: boolean;
  releveAt: string | null;
  releveAgeMin: number | null;
  freeGb: number | null;
  tasks: { id: string; label: string; ok: boolean; lastOk: string | null }[];
  checks: { id: string; level: BackupLevel; label: string }[];
  alerts: { id: string; label: string }[];
}

/** 401 ou 403 : pas de droit sur cette route, inutile de réessayer. */
export class BackupStatusDeniedError extends Error {}

const LEVELS: BackupLevel[] = ['green', 'orange', 'red', 'gray'];

function parse(json: unknown): BackupStatus {
  const data = (json as { success?: boolean; data?: Partial<BackupStatus> } | null)?.data;
  if (!data || typeof data !== 'object' || !LEVELS.includes(data.level as BackupLevel)) {
    throw new Error('Réponse inattendue');
  }
  return {
    schema: Number(data.schema) || 1,
    generatedAt: String(data.generatedAt ?? ''),
    level: data.level as BackupLevel,
    reason: data.reason === 'no_releve' || data.reason === 'off_hours' ? data.reason : null,
    summary: String(data.summary ?? ''),
    openDay: Boolean(data.openDay),
    inService: Boolean(data.inService),
    releveAt: typeof data.releveAt === 'string' ? data.releveAt : null,
    releveAgeMin: typeof data.releveAgeMin === 'number' ? data.releveAgeMin : null,
    freeGb: typeof data.freeGb === 'number' ? data.freeGb : null,
    tasks: Array.isArray(data.tasks) ? data.tasks : [],
    checks: Array.isArray(data.checks) ? data.checks : [],
    alerts: Array.isArray(data.alerts) ? data.alerts : [],
  };
}

export const backupStatusService = {
  async get(): Promise<BackupStatus> {
    const res = await authenticatedFetch('/api/ops/status');
    if (res.status === 401 || res.status === 403) {
      throw new BackupStatusDeniedError('Accès refusé');
    }
    if (!res.ok) throw new Error(`État indisponible (${res.status})`);
    return parse(await res.json());
  },
};
